import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg } from "@/lib/api";

export const ACTIONS = {
  hold: { title: "Hold shipment", help: "Temporarily stop processing or dispatch.", button: "Place on hold", cls: "bg-amber-500 hover:bg-amber-600" },
  release: { title: "Release shipment", help: "Remove the hold and resume processing.", button: "Release", cls: "bg-emerald-600 hover:bg-emerald-700" },
  proceed: { title: "Proceed with shipment", help: "Authorize the shipment to move to its next stage.", button: "Proceed", cls: "bg-[#0A192F] hover:bg-[#112240]" },
  cancel: { title: "Cancel shipment", help: "This stops the shipment for good.", button: "Cancel shipment", cls: "bg-red-600 hover:bg-red-700" },
};

export function ActionDialog({ action, shipment, nextStage, onOpenChange, onDone }) {
  const api = useApi();
  const [f, setF] = useState({ reason: "", location: "", note: "", notify: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (action) setF({ reason: "", location: "", note: "", notify: true }); }, [action]);
  const cfg = ACTIONS[action];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("post", `/admin/shipments/${shipment.id}/${action}`, f);
      toast.success(`${cfg.title}: done${r.email === "sent" ? ". Customer notified." : ""}`);
      onDone();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!action} onOpenChange={onOpenChange}>
      {cfg && (
        <DialogContent className="max-w-md" data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{cfg.title} · <span className="font-mono text-[#FF6B00]">{shipment?.tracking_number}</span></DialogTitle>
            <DialogDescription>{cfg.help}{action === "proceed" && nextStage ? ` Next stage: ${nextStage}.` : ""}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label>Reason (required, kept in the activity history)</Label>
              <Textarea required maxLength={500} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="action-reason-input" /></div>
            <div className="space-y-1.5"><Label>Location (optional)</Label>
              <Input placeholder={shipment?.current_location?.name || ""} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="action-location-input" /></div>
            <div className="space-y-1.5"><Label>Message shown on the tracking page (optional)</Label>
              <Input maxLength={500} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="action-note-input" /></div>
            <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
              <p className="text-sm font-medium">Email the customer and receiver</p>
              <Switch checked={f.notify} onCheckedChange={(v) => setF({ ...f, notify: v })} data-testid="action-notify-switch" />
            </div>
            <Button type="submit" disabled={busy} className={`w-full text-white ${cfg.cls}`} data-testid="action-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} {cfg.button}
            </Button>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );
}
